import { getDeferredDeepLinkFingerprint } from "@juisci/device-fingerprint";
import { z } from "zod";
import { requestInstallContextJson } from "./installContextHttp";

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 500;
const storeUserFingerprintResponseSchema = z.object({ id: z.string().min(1) });

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}

/**
 * Stores a user fingerprint with destination URL for deferred deep linking
 * This should be called when a user clicks a link on the web before installing the app
 */
export async function storeUserFingerprint(
  destinationUrl: string
): Promise<void> {
  let clientHash: string;
  try {
    const fingerprintData = await getDeferredDeepLinkFingerprint();
    clientHash = fingerprintData.fingerprint;
  } catch (error) {
    console.error(
      `DDL: failed to generate fingerprint before store request destinationUrl=${destinationUrl} error="${getErrorMessage(error)}"`
    );
    return;
  }

  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    try {
      const response = await requestInstallContextJson(
        "/webapp/public/install-context/store",
        {
          clientHash,
          destinationUrl,
        },
        (raw) => storeUserFingerprintResponseSchema.parse(raw)
      );
      if (!response) {
        throw new Error("DDL: store endpoint unexpectedly returned 404");
      }
      return;
    } catch (error) {
      console.error(
        `DDL: store fingerprint request failed attempt=${attempt + 1}/${MAX_RETRIES} destinationUrl=${destinationUrl} error="${getErrorMessage(error)}"`
      );
      if (attempt < MAX_RETRIES - 1) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      }
    }
  }
  console.error(
    `DDL: failed to store user fingerprint after retries destinationUrl=${destinationUrl}`
  );
}
